import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import {
  useMonkeysEnvironment,
  useMonkeysPortalOptions,
} from '../../provider/context';
import type { BasePortalContainer, BasePortalProps } from './types';

function resolveContainerValue(
  value: BasePortalContainer,
  source: string,
): Element | null {
  const candidate = typeof value === 'function' ? value() : value;
  if (candidate === null) return null;
  if (
    !candidate ||
    typeof candidate !== 'object' ||
    candidate.nodeType !== 1
  ) {
    throw new TypeError(`${source} must resolve to an Element or null.`);
  }
  return candidate;
}

interface ResolveBasePortalContainerOptions {
  explicitProvided: boolean;
  explicitContainer?: BasePortalContainer;
  providerProvided: boolean;
  providerContainer?: BasePortalContainer;
  getDocument?: () => Document | null;
}

export function resolveBasePortalContainer({
  explicitProvided,
  explicitContainer,
  providerProvided,
  providerContainer,
  getDocument,
}: ResolveBasePortalContainerOptions): Element | null {
  if (explicitProvided) {
    return resolveContainerValue(
      explicitContainer as BasePortalContainer,
      'BasePortal container',
    );
  }
  if (providerProvided) {
    return resolveContainerValue(
      providerContainer as BasePortalContainer,
      'MonkeysProvider portal.container',
    );
  }
  if (!getDocument) return null;

  const ownerDocument = getDocument();
  if (ownerDocument === null) return null;
  if (
    !ownerDocument ||
    typeof ownerDocument !== 'object' ||
    !('body' in ownerDocument)
  ) {
    throw new TypeError(
      'MonkeysProvider environment.getDocument must resolve to a Document or null.',
    );
  }
  return resolveContainerValue(
    ownerDocument.body,
    'MonkeysProvider environment.getDocument().body',
  );
}

export function BasePortal(props: BasePortalProps) {
  const environment = useMonkeysEnvironment();
  const portalOptions = useMonkeysPortalOptions();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (props.disabled !== undefined && typeof props.disabled !== 'boolean') {
    throw new TypeError('BasePortal disabled must be a boolean.');
  }
  if (props.inline !== undefined && typeof props.inline !== 'boolean') {
    throw new TypeError('BasePortal inline must be a boolean.');
  }

  const disabled = props.disabled ?? portalOptions.disabled ?? false;
  if (!mounted || disabled || props.inline) {
    return <>{props.children}</>;
  }

  const container = resolveBasePortalContainer({
    explicitProvided: props.container !== undefined,
    explicitContainer: props.container,
    providerProvided: portalOptions.container !== undefined,
    providerContainer: portalOptions.container,
    getDocument: environment.getDocument,
  });

  return container
    ? createPortal(props.children, container)
    : <>{props.children}</>;
}
